/**
 * JWT token utilities — pure, no React, no side effects.
 *
 * Used by AuthContext for client-side expiry UX. The server remains the
 * authority on token validity (401 responses); these helpers exist so the
 * client can preempt the round-trip and present accurate UI.
 *
 * Behavior contract:
 * - Non-JWT tokens (e.g. opaque tokens) are treated as non-expiring.
 *   getTokenExpiration returns null; hasTokenExpired returns false. This
 *   keeps the client functional if the API ever switches token formats.
 * - Malformed JWTs and JWTs without an `exp` claim are likewise treated
 *   as non-expiring. We never throw.
 * - No signature verification — that is the server's job.
 */

interface JwtPayload {
  exp?: number;
}

/**
 * Reads the JWT payload (no signature verification) and returns the `exp`
 * claim as a millisecond timestamp.
 *
 * Returns null when:
 * - token is not a 3-part JWT
 * - base64 payload cannot be decoded or parsed as JSON
 * - payload has no numeric `exp` claim
 */
export const getTokenExpiration = (token: string): number | null => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '='
    );
    const payload = JSON.parse(atob(padded)) as JwtPayload;
    if (typeof payload.exp !== 'number') {
      return null;
    }
    return payload.exp * 1000;
  } catch {
    return null;
  }
};

/**
 * True if the token exists and its `exp` claim (when present) is in the past.
 *
 * - null token → false (no token, nothing to expire)
 * - non-JWT token → false (treated as non-expiring)
 * - JWT without exp → false
 * - JWT with future exp → false
 * - JWT with past exp → true
 */
export const hasTokenExpired = (token: string | null): boolean => {
  if (!token) {
    return false;
  }
  const expiresAt = getTokenExpiration(token);
  if (!expiresAt) {
    return false;
  }
  return Date.now() >= expiresAt;
};
